/**
 * IssueBriefCard — Apple-simple consolidated brief for the Resolve Issue modal.
 *
 * One card. One glance. Replaces the previous stack of:
 *   • PushContextHeader (3-stat strip)
 *   • Homeowner & site card
 *   • Reported description card
 *   • Complex-issue / "Issue Detail" expander
 *
 * Layout (top to bottom):
 *   1. Customer header — name, contact, address, scheduled date
 *   2. Three stat tiles — At Stake · Last Contact · Top Blocker
 *   3. Reported — description + structured chips (Blocker · Area · Stage · Owner)
 *   4. Homeowner concern (if present) — green-tinted callout
 *   5. Suggested action (if present) — blue-tinted callout
 *
 * Pure presentational. No mutations, no expander, no duplication.
 */
import { useMemo } from "react";
import { DollarSign, Clock, AlertTriangle, MapPin, Copy, ExternalLink, Calendar, ListChecks } from "lucide-react";
import type { Job, Issue } from "@/data/types";
import { useFinancialPermissions } from "@/hooks/useFinancialPermissions";
import { ContactBlock } from "@/components/quick-actions/ContactBlock";
import { cn } from "@/lib/utils";

interface Props {
  job: Job;
  issue: Issue;
  openIssues?: Issue[];
  onPhoneClick?: () => void;
  onEmailClick?: () => void;
  onCopyAddress?: () => void;
}

function daysSince(iso?: string): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
}

function fmtCurrency(n: number): string {
  if (n >= 1000) return `$${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return `$${n.toFixed(0)}`;
}

export function IssueBriefCard({ job, issue, openIssues = [], onPhoneClick, onEmailClick, onCopyAddress }: Props) {
  const fin = useFinancialPermissions();

  const lastContactDays = useMemo(() => {
    const history = job.activityHistory ?? [];
    const touch = [...history].reverse().find((h) => /call|sms|text|email|message|contact|note/i.test(h.action));
    return daysSince(touch?.date);
  }, [job.activityHistory]);

  const biggestBlocker = useMemo(() => {
    const urgent = openIssues.find((i) => i.severity === "Urgent");
    if (urgent) return { label: urgent.issueType, tone: "critical" as const };
    const medium = openIssues.find((i) => i.severity === "Medium");
    if (medium) return { label: medium.issueType, tone: "high" as const };
    if (openIssues.length > 0) {
      return { label: `${openIssues.length} open issue${openIssues.length > 1 ? "s" : ""}`, tone: "medium" as const };
    }
    if (!job.scheduledInstallDate) return { label: "No install date", tone: "medium" as const };
    if (job.assignmentType === "Unassigned" || (!job.assignedCrew && !job.assignedSubcontractor)) {
      return { label: "No crew assigned", tone: "medium" as const };
    }
    return { label: "On track", tone: "ok" as const };
  }, [openIssues, job.scheduledInstallDate, job.assignmentType, job.assignedCrew, job.assignedSubcontractor]);

  const contractValue = job.financials?.contractValue;
  const showRevenue = fin.viewContractValue && typeof contractValue === "number" && contractValue > 0;

  const contactTone =
    lastContactDays === null ? "muted" :
    lastContactDays >= 5 ? "critical" :
    lastContactDays >= 3 ? "high" : "ok";

  const accent =
    issue.severity === "Urgent"
      ? "border-l-destructive"
      : issue.severity === "Medium"
      ? "border-l-warning"
      : "border-l-primary";

  // Detail chips — only render the ones we actually have.
  const chips = [
    issue.blockerCategory ? { label: "Blocker", value: issue.blockerCategory } : null,
    issue.affectedArea ? { label: "Area", value: issue.affectedArea } : null,
    issue.jobStageAtCreation ? { label: "Stage", value: issue.jobStageAtCreation } : null,
    issue.owner ? { label: "Owner", value: issue.owner } : null,
  ].filter(Boolean) as { label: string; value: string }[];

  return (
    <div className={cn("rounded-xl border border-l-4 bg-card shadow-sm overflow-hidden", accent)}>
      {/* 1. Customer header */}
      <div className="px-4 pt-3.5 pb-3 space-y-2">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="text-base font-semibold tracking-tight truncate">{job.customerName}</h3>
          <span className="text-[10px] font-mono text-muted-foreground shrink-0">{job.jobNumber || job.id}</span>
        </div>

        <ContactBlock
          name={job.customerName}
          phone={job.phone}
          email={job.email}
          emailSubject={`Update on your project — ${job.customerName}`}
          onPhoneClick={onPhoneClick}
          onEmailClick={onEmailClick}
          compact
        />

        {job.address && (
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="truncate flex-1">
              {job.address.street}, {job.address.city}
            </span>
            {onCopyAddress && (
              <button
                type="button"
                onClick={onCopyAddress}
                className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded hover:bg-muted transition-colors"
                title="Copy address"
              >
                <Copy className="h-2.5 w-2.5" />
              </button>
            )}
            <a
              href={`https://maps.google.com/?q=${encodeURIComponent(`${job.address.street}, ${job.address.city}`)}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded hover:bg-muted transition-colors"
              title="Open in Maps"
            >
              <ExternalLink className="h-2.5 w-2.5" />
            </a>
          </div>
        )}

        {job.scheduledInstallDate && (
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Calendar className="h-3 w-3 shrink-0" />
            <span>Scheduled {new Date(job.scheduledInstallDate).toLocaleDateString()}</span>
            {job.assignedSubcontractor && <span>· {job.assignedSubcontractor}</span>}
          </div>
        )}
      </div>

      {/* 2. Stat strip */}
      <div className="grid grid-cols-3 border-y bg-muted/20">
        <Stat
          icon={<DollarSign className="h-3.5 w-3.5" />}
          label="At Stake"
          value={showRevenue ? fmtCurrency(contractValue!) : "—"}
          tone="neutral"
        />
        <Stat
          icon={<Clock className="h-3.5 w-3.5" />}
          label="Last Contact"
          value={lastContactDays === null ? "No record" : lastContactDays === 0 ? "Today" : `${lastContactDays}d ago`}
          tone={contactTone === "critical" ? "critical" : contactTone === "high" ? "warning" : "neutral"}
          divider
        />
        <Stat
          icon={<AlertTriangle className="h-3.5 w-3.5" />}
          label="Top Blocker"
          value={biggestBlocker.label}
          tone={
            biggestBlocker.tone === "critical" ? "critical" :
            biggestBlocker.tone === "high" ? "warning" :
            biggestBlocker.tone === "ok" ? "success" : "neutral"
          }
          divider
        />
      </div>

      {/* 3. Reported — description + integrated detail chips */}
      <div className="px-4 py-3 space-y-2.5">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">Reported</p>
          <p className="text-sm leading-relaxed">{issue.description}</p>
        </div>

        {chips.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-0.5">
            {chips.map((c) => (
              <span
                key={c.label}
                className="inline-flex items-baseline gap-1 rounded-md border bg-muted/30 px-2 py-0.5 text-[11px]"
              >
                <span className="text-muted-foreground">{c.label}</span>
                <span className="font-medium text-foreground">{c.value}</span>
              </span>
            ))}
          </div>
        )}

        {issue.homeownerConcern && (
          <div className="rounded-md border border-success/20 bg-success/5 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-success/90 mb-0.5">
              Homeowner concern
            </p>
            <p className="text-xs leading-relaxed text-foreground">{issue.homeownerConcern}</p>
          </div>
        )}

        {/* Suggested action intentionally omitted here — the SuggestedMoveCard
            hero in the Resolve drawer is the single source of "what to do next".
            Keeping both produced duplicate intent. */}
      </div>
    </div>
  );
}

interface StatProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone: "neutral" | "warning" | "critical" | "success";
  divider?: boolean;
}

function Stat({ icon, label, value, tone, divider }: StatProps) {
  const valueClass =
    tone === "critical" ? "text-destructive" :
    tone === "warning"  ? "text-warning" :
    tone === "success"  ? "text-success" :
    "text-foreground";
  const iconClass =
    tone === "critical" ? "text-destructive" :
    tone === "warning"  ? "text-warning" :
    tone === "success"  ? "text-success" :
    "text-muted-foreground";

  return (
    <div className={cn("px-3 py-2.5 min-w-0", divider && "border-l")}>
      <div className="flex items-center gap-1.5 mb-0.5">
        <span className={iconClass}>{icon}</span>
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">{label}</span>
      </div>
      <div className={cn("text-sm font-semibold truncate", valueClass)}>{value}</div>
    </div>
  );
}
